import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { marked } from 'marked';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface ResponseVersion {
  version: number;
  response_text: string;
  created_at: string;
}

interface ResponseVersionCarouselProps {
  versions: ResponseVersion[];
  currentVersion: number;
}

export const ResponseVersionCarousel: React.FC<ResponseVersionCarouselProps> = ({
  versions,
  currentVersion
}) => {
  const [activeIndex, setActiveIndex] = useState(versions.length - 1); // Start with latest version

  if (!versions || versions.length <= 1) {
    return null; // Don't show carousel if only 1 or no versions
  }

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : versions.length - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev < versions.length - 1 ? prev + 1 : 0));
  };

  const activeVersion = versions[activeIndex];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Version {activeIndex + 1} of {versions.length}
          {activeIndex === versions.length - 1 && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
              Latest
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={versions.length <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={versions.length <= 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div 
            className="text-sm leading-relaxed break-words [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:mb-3 [&>ol]:ml-4 [&>ol]:list-decimal [&>strong]:font-semibold [&>em]:italic [&>h1]:text-lg [&>h1]:font-bold [&>h1]:mb-2 [&>h2]:text-base [&>h2]:font-bold [&>h2]:mb-2 [&>h3]:text-sm [&>h3]:font-bold [&>h3]:mb-2"
            dangerouslySetInnerHTML={{ 
              __html: marked.parse(activeVersion.response_text) as string
            }}
          />
          <div className="mt-4 text-xs text-muted-foreground">
            Generated: {new Date(activeVersion.created_at).toLocaleString()}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
